import ClearIcon from '@mui/icons-material/Clear';
import { Chip, SelectChangeEvent } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import useCepList from './CepList.hook';
import {
  filtersSectionStyle,
  filtersHeaderStyle,
  filtersTitleStyle,
  filtersResultsStyle,
  filtersResultsValueStyle,
  filtersFormStyle,
  selectedTagsStyle,
  resetButtonStyle,
  sortSelectWrapperStyle,
  sortSelectStyle,
} from './CepList.style';
import { AlertMessageType } from 'basics/enums/maya.enums';
import { SelectOption } from 'basics/options/options.types';
import AlertBanner from 'components/AlertBanner/AlertBanner';
import { FormLayoutContainer } from 'components/FormLayout';
import GridList from 'components/GridList/GridList';
import ClearAllIcon from 'components/Icons/ClearAllIcon';
import MayaLayout from 'components/MayaLayout';
import TacticFieldsValidationDialog from 'components/TacticFieldsValidationDialog';
import TacticValidationDialog from 'components/TacticValidationDialog/TacticValidationDialog';
import Box from 'yoda-ui/components/Box';
import ConfirmationDialog from 'yoda-ui/components/Dialogs/ConfirmationDialog';
import Select from 'yoda-ui/components/Form/Select';
import MultiSelectYoda from 'yoda-ui/components/Form/SelectYoda/MultiSelectYoda';
import { TagStyle } from 'yoda-ui/components/Form/SelectYoda/MultiSelectYoda/MultiSelectYoda.styles';
import TextYoda from 'yoda-ui/components/Form/TextYoda';
import Loader from 'yoda-ui/components/Loader';
import MenuItem from 'yoda-ui/components/MenuItem';
import { YodaFormProvider } from 'yoda-ui/yodaForm';
import { YodaColors, YodaFontSize } from 'yoda-ui/yodaTheme';

const CepList: FC = () => {
  const { t } = useTranslation();
  const flags = useFlags();

  const isCepFilterByTAreaProductFunctionEnabled = flags.mayaAkma419EnableSiCiCepListAdditionalFilters;

  const {
    cepListColumnConfig,
    cepLoading,
    cepTitleFieldConfig,
    customerInitiativeFieldConfig,
    debouncedHandleChangeCepSearch,
    cepTherapeuticAreaFieldConfig,
    handleChangeCepTherapeuticArea,
    cepProductFieldConfig,
    handleChangeCepProduct,
    cepBiogenFunctionFieldConfig,
    handleChangeCepBiogenFunction,
    getFilteredRows,
    handleChangeCI,
    handleChangeStatus,
    handleClick,
    menuCustomerEngagementPlanningLabel,
    newElementButton,
    onPageChange,
    onPageSizeChange,
    open,
    removeCepLoading,
    cancelCepLoading,
    restoreCepLoading,
    rowCountState,
    setOpen,
    statusFieldConfig,
    confirmationDialogConfig,
    openTacticValidationDialog,
    setOpenTacticValidationDialog,
    openFieldsValidationDialog,
    setOpenFieldsValidationDialog,
    invalidTactics,
    cepDateError,
    validationError,
    selectedCepId,
    generalLoading,
    providerFields,
    selectedFilters,
    handleRemoveChip,
    handleReset,
    hasSelectedFilters,
    cepSortFieldConfig,
    handleSortChange,
    hasActiveFilters,
    cepSortFilter,
  } = useCepList();

  return (
    <MayaLayout>
      <AlertBanner type={ AlertMessageType.VeevaFatalErrorAlert } />
      <GridList
        loading={ cepLoading || generalLoading }
        getRowId={ (row) => row._id }
        onCellClick={ handleClick }
        title={ menuCustomerEngagementPlanningLabel }
        columnConfig={ cepListColumnConfig }
        rows={ getFilteredRows() }
        onPageChange={ onPageChange }
        onPageSizeChange={ onPageSizeChange }
        rowCountState={ rowCountState }
        newElementButton={ newElementButton }
        customFilters={
          (
            <YodaFormProvider { ...providerFields }>
              <Box sx={ filtersSectionStyle }>
                <Box sx={ filtersHeaderStyle }>
                  <Box sx={ filtersTitleStyle }>{ t('maya_ci_filter_title') }</Box>
                  {
                    !cepLoading && (
                      <Box sx={ filtersResultsStyle }>
                        { t('maya_ci_filter_results_title') }<Box
                          component="span"
                          sx={ filtersResultsValueStyle }>
                          { t('maya_ci_filter_results_value', { shownItems: getFilteredRows().length, allItems: rowCountState }) }
                        </Box>
                      </Box>
                    )
                  }
                </Box>

                <FormLayoutContainer sx={ filtersFormStyle }>
                  <Box>
                    <TextYoda
                      onChange={ debouncedHandleChangeCepSearch }
                      { ...cepTitleFieldConfig } />
                  </Box>
                  <Box>
                    <MultiSelectYoda
                      onChange={ handleChangeStatus }
                      { ...statusFieldConfig } />
                  </Box>
                  <Box>
                    <MultiSelectYoda
                      onChange={ handleChangeCI }
                      { ...customerInitiativeFieldConfig } />
                  </Box>
                  {
                    isCepFilterByTAreaProductFunctionEnabled && <Box>
                      <MultiSelectYoda
                        onChange={ handleChangeCepTherapeuticArea }
                        { ...cepTherapeuticAreaFieldConfig } />
                    </Box>
                  }
                  {
                    isCepFilterByTAreaProductFunctionEnabled && <Box>
                      <MultiSelectYoda
                        onChange={ handleChangeCepProduct }
                        { ...cepProductFieldConfig } />
                    </Box>
                  }
                  {
                    isCepFilterByTAreaProductFunctionEnabled && <Box>
                      <MultiSelectYoda
                        onChange={ handleChangeCepBiogenFunction }
                        { ...cepBiogenFunctionFieldConfig } />
                    </Box>
                  }
                </FormLayoutContainer>

                {
                  hasActiveFilters
                  && <Box sx={ selectedTagsStyle }>
                    {
                      selectedFilters.status.map((option) => (
                        <Chip
                          key={ option.id }
                          label={ option.label }
                          sx={ TagStyle }
                          onDelete={ () => handleRemoveChip('status', option.id) }
                          deleteIcon={ <ClearIcon /> }
                        />
                      ))
                    }
                    {
                      selectedFilters.customerInitiative.map((option) => (
                        <Chip
                          key={ option.id }
                          label={ option.label }
                          sx={ TagStyle }
                          onDelete={ () => handleRemoveChip('customerInitiative', option.id) }
                          deleteIcon={ <ClearIcon /> }
                        />
                      ))
                    }
                    {
                      selectedFilters.therapeuticArea.map((option) => (
                        <Chip
                          key={ option.id }
                          label={ option.label }
                          sx={ TagStyle }
                          onDelete={ () => handleRemoveChip('therapeuticArea', option.id) }
                          deleteIcon={ <ClearIcon /> }
                        />
                      ))
                    }
                    {
                      selectedFilters.product.map((option) => (
                        <Chip
                          key={ option.id }
                          label={ option.label }
                          sx={ TagStyle }
                          onDelete={ () => handleRemoveChip('product', option.id) }
                          deleteIcon={ <ClearIcon /> }
                        />
                      ))
                    }
                    {
                      selectedFilters.cepFunction.map((option) => (
                        <Chip
                          key={ option.id }
                          label={ option.label }
                          sx={ TagStyle }
                          onDelete={ () => handleRemoveChip('cepFunction', option.id) }
                          deleteIcon={ <ClearIcon /> }
                        />
                      ))
                    }
                    {
                      hasSelectedFilters && (
                        <Box onClick={ handleReset } sx={ resetButtonStyle }>
                          <ClearAllIcon />
                          { t('actions_reset_label') }
                        </Box>
                      )
                    }
                  </Box>
                }
              </Box>
              <Box sx={ sortSelectWrapperStyle }>
                <Select
                  value={ cepSortFilter }
                  onChange={
                    (event: SelectChangeEvent<unknown>, _child: ReactNode) => {
                      const selectedOption = cepSortFieldConfig.options.find((opt) => opt.id === event.target.value);
                      if (selectedOption) {
                        handleSortChange(selectedOption);
                      }
                    }
                  }
                  sx={ sortSelectStyle }
                >
                  {
                    cepSortFieldConfig.options.map(
                      (option: SelectOption) => {
                        return <MenuItem key={ option.id }
                          value={ option.id } sx={ { fontSize: YodaFontSize.small } }>{ option.label }</MenuItem>;
                      },
                    )
                  }
                </Select>
              </Box>
            </YodaFormProvider>
          )
        }
        disableColumnMenu={ true }
      />
      {
        open && (
          <ConfirmationDialog
            { ...confirmationDialogConfig }
            onConfirmLabel={
              removeCepLoading || cancelCepLoading || restoreCepLoading
                ? <Loader center size={ 1 } color={ YodaColors.inherit } /> : t('actions_confirm_label')
            }
            onDismiss={ () => setOpen(false) }
            onDismissLabel={ t('actions_cancel_label') }
          >
          </ConfirmationDialog>
        )
      }
      <TacticValidationDialog
        title={ t('maya_tactic_form_validation_dialog_title') }
        open={ openTacticValidationDialog }
        setOpen={ setOpenTacticValidationDialog }
        cepId={ selectedCepId }
      >
      </TacticValidationDialog>
      <TacticFieldsValidationDialog
        open={ openFieldsValidationDialog }
        setOpen={ setOpenFieldsValidationDialog }
        invalidTactics={ invalidTactics }
        cepDateError={ cepDateError }
        validationError={ validationError }
        cepId={ selectedCepId }
      />
    </MayaLayout>
  );
};

export default CepList;
